import api from "./api";

const IMG_MAX = 2000;
const IMG_SOGLIA = 600 * 1024;
const POLL_TIMEOUT = 5 * 60 * 1000;

const isImg = (f) => (f.type || "").startsWith("image/") && !/heic|heif/i.test(f.type + f.name);

/** Riduce nel browser le foto (max 2000 px, JPEG 85%) prima dell'upload: molto più veloce da rete mobile. */
export async function comprimiImmagine(file) {
  if (!isImg(file)) return file;
  try {
    const bmp = await createImageBitmap(file);
    const { width: w, height: h } = bmp;
    if (Math.max(w, h) <= IMG_MAX && file.size <= IMG_SOGLIA) { bmp.close?.(); return file; }
    const r = Math.min(1, IMG_MAX / Math.max(w, h));
    const c = document.createElement("canvas"); c.width = Math.round(w * r); c.height = Math.round(h * r);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height); bmp.close?.();
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

/** Messaggio in italiano per gli errori di caricamento/lettura AI. */
export function spiegaErrore(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (e?.code === "ECONNABORTED") return "Caricamento troppo lento: controlla la connessione e riprova";
  if (e?.response?.status === 413) return "File troppo grande per il server (max 25 MB)";
  if (e?.message && !/network|request failed|status code/i.test(e.message)) return e.message;
  return "Lettura AI non riuscita: riprova";
}

/**
 * Carica un file e lo fa leggere all'AI in background (nessun timeout del proxy).
 * onProgress({ fase: "upload"|"lettura"|"ok", pct, secondi, pagine }).
 * Ritorna l'array dei documenti letti: [{ ...risposta_endpoint, pagine: [..]|null }]. Un file "normale" → 1 elemento.
 */
// ---- tracciamento file → lettura (job) per lo storico "Letture AI" e per riaprire una lettura senza ripagarla
const jobs = new WeakMap();
export const registraJob = (file, jobId) => { if (file && jobId) jobs.set(file, jobId); };
export const jobDiFile = (file) => (file ? jobs.get(file) : undefined);
/** Letture che compilano solo un form (nessun record con allegato): si considerano "usate" appena consegnate. */
const autoUsato = (tipo) => /^(extract|import)\//.test(tipo) || tipo === "ai/documento-extract" || /estrai-(operaio|certificazione|scadenza-mezzo)$/.test(tipo);
/** Segna la lettura come utilizzata (record creati). Accetta il File originale o il job id. */
export async function segnaUsato(fileOJob, n = 1) {
  const id = typeof fileOJob === "string" ? fileOJob : jobDiFile(fileOJob);
  if (!id) return;
  try { await api.post(`/ai/uploads/${id}/usato`, { n }); } catch { /* best effort */ }
}

export async function aiEstrai(tipo, file, { params, onProgress } = {}) {
  const prog = (p) => onProgress?.(p);
  let start = null;
  const esistente = jobDiFile(file);
  if (esistente) {
    // lettura già fatta (riapertura dallo storico): se era in errore la rilancio sul server, altrimenti leggo il risultato salvato
    const { data: job } = await api.get(`/ai/jobs/${esistente}`, { cache: false });
    start = job.stato === "error" ? (await api.post(`/ai/uploads/${esistente}/riprova`)).data : { job_id: esistente, pagine: job.pagine };
  } else {
    const f = await comprimiImmagine(file);
    prog({ fase: "upload", pct: 0, secondi: 0 });
    const fd = new FormData(); fd.append("file", f); fd.append("tipo", tipo);
    fd.append("params", JSON.stringify({ ...(params || {}), origine: typeof window !== "undefined" ? window.location.pathname + window.location.search : "" }));
    start = (await api.post("/ai/estrai-job", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      onUploadProgress: (ev) => prog({ fase: "upload", pct: ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50, secondi: 0 }),
    })).data;
    registraJob(file, start.job_id);
  }
  const t0 = Date.now(); let attesa = 900;
  prog({ fase: "lettura", pct: 100, secondi: 0, pagine: start.pagine });
  while (Date.now() - t0 < POLL_TIMEOUT) {
    await new Promise((r) => setTimeout(r, attesa)); attesa = Math.min(Math.round(attesa * 1.3), 3000);
    const { data: job } = await api.get(`/ai/jobs/${start.job_id}`, { cache: false });
    prog({ fase: "lettura", pct: 100, secondi: Math.round((Date.now() - t0) / 1000), pagine: job.pagine ?? start.pagine });
    if (job.stato === "done") {
      prog({ fase: "ok", pct: 100, secondi: Math.round((Date.now() - t0) / 1000) });
      if (autoUsato(tipo)) segnaUsato(start.job_id, 1);
      return (job.risultato?.documenti || []).map((d) => ({ ...d, _job: start.job_id }));
    }
    if (job.stato === "error") throw new Error(job.errore || "Lettura AI non riuscita: riprova");
  }
  throw new Error("La lettura AI sta richiedendo troppo tempo: riprova con un file più leggero");
}

/** Versione a documento singolo: ritorna la risposta dell'endpoint (come le vecchie chiamate dirette). */
export async function aiEstraiUno(tipo, file, opts) {
  const docs = await aiEstrai(tipo, file, opts);
  if (!docs.length) throw new Error("L'AI non ha trovato dati nel documento");
  return docs[0];
}

/** Da un PDF con più documenti, ottiene il PDF delle sole pagine di uno di essi (per allegarlo al record giusto). */
export async function fileConPagine(file, pagine) {
  if (!pagine?.length || !file) return file;
  const fd = new FormData(); fd.append("file", file); fd.append("pagine", pagine.join(","));
  const { data } = await api.post("/ai/pdf-pagine", fd, { headers: { "Content-Type": "multipart/form-data" }, responseType: "blob", timeout: 60000 });
  const base = file.name.replace(/\.pdf$/i, "");
  return new File([data], `${base} - pag ${pagine.join("-")}.pdf`, { type: "application/pdf" });
}

export const testoFase = (p) => !p ? "" : p.fase === "upload" ? `Caricamento ${p.pct}%` : p.fase === "lettura" ? `Lettura AI${p.pagine > 1 ? ` · ${p.pagine} pagine` : ""}${p.secondi ? ` · ${p.secondi} s` : "…"}` : p.fase === "ok" ? "Pronto" : "";
